import { SelectCountryModal, VerifyPhoneModal } from '@/components/modal'
import { useResendVerifyPhone } from '@/services'
import { Country } from '@/types'
import moment from 'moment'
import { Dispatch, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

type VerifyPhoneInputProps = {
  name?: string
  label: string
  placeholder?: string
  selectedCountry: Country
  setSelectedCountry: Dispatch<React.SetStateAction<Country>>
  userPhoneNumber: string
  phoneNumber: string
  setPhoneNumber: (phone: string) => void
  maxLength: number
  className?: string
  isValidPhone: boolean
  setIsValidPhone: Dispatch<React.SetStateAction<boolean>>
  resendVerifyCountDownMinutes: number
  error: string | undefined
  isVerifyPhoneModalOpened: boolean
  setIsVerifyPhoneModalOpened: Dispatch<React.SetStateAction<boolean>>
}

const LOCAL_RESEND_END_TIME = 'resendEndTime'
const LOCAL_IS_RESEND_COUNTING = 'isResendCounting'

export const VerifyPhoneInput = (props: VerifyPhoneInputProps) => {
  const { t } = useTranslation()

  const [resendMinutes, setResendMinutes] = useState<number>(props.resendVerifyCountDownMinutes)
  const [resendSeconds, setResendSeconds] = useState<number>(0)
  const [isResendCounting, setIsResendCounting] = useState<boolean>(false)
  const [isOpenCountry, setIsOpenCountry] = useState(false)

  const { mutate: resendVerifyPhone } = useResendVerifyPhone()

  const toggleModal = () => setIsOpenCountry(!isOpenCountry)

  const onChangeCountry = (country: Country) => {
    props.setSelectedCountry(country)
  }

  const onChangePhoneNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /^[0-9]*$/
    if (regex.test(e.target.value)) {
      props.setPhoneNumber(e.target.value)
    }
  }

  useEffect(() => {
    if (props.isVerifyPhoneModalOpened) {
      const localResendEndTime = moment().add(props.resendVerifyCountDownMinutes, 'minutes').toISOString()
      setIsResendCounting(true)
      localStorage.setItem(LOCAL_RESEND_END_TIME, localResendEndTime)
      localStorage.setItem(LOCAL_IS_RESEND_COUNTING, JSON.stringify(true))
      resendVerifyPhone({ phoneNumber: `${props.userPhoneNumber}` })
    }
  }, [props.isVerifyPhoneModalOpened, props.resendVerifyCountDownMinutes, props.userPhoneNumber, resendVerifyPhone])

  const onSendVerifyClick = () => {
    props.setIsVerifyPhoneModalOpened(true)
  }

  // const countDownDisplay = (minutes: number, seconds: number) => {
  //   return `${minutes < 10 ? `0${minutes}` : minutes}:${seconds < 10 ? `0${seconds}` : seconds}`
  // }

  const resetCountDown = useCallback(() => {
    localStorage.removeItem(LOCAL_IS_RESEND_COUNTING)
    localStorage.removeItem(LOCAL_RESEND_END_TIME)
    setResendMinutes(props.resendVerifyCountDownMinutes)
    setResendSeconds(0)
  }, [props.resendVerifyCountDownMinutes])

  useEffect(() => {
    const localResendEndTime = localStorage.getItem(LOCAL_RESEND_END_TIME)
    const localisVerifyDigitSent = localStorage.getItem(LOCAL_IS_RESEND_COUNTING)

    const remainingCountDownTime = moment(localResendEndTime).diff(moment.now())
    const remainingDuration = moment.duration(remainingCountDownTime)

    if (
      localisVerifyDigitSent &&
      remainingDuration.asMinutes() <= props.resendVerifyCountDownMinutes &&
      remainingDuration.asMinutes() > 0
    ) {
      setIsResendCounting(!!localisVerifyDigitSent)
      setResendMinutes(remainingDuration.minutes())
      setResendSeconds(remainingDuration.seconds())
    } else {
      resetCountDown()
    }
  }, [props.resendVerifyCountDownMinutes, resetCountDown])

  useEffect(() => {
    let myInterval: any
    if (isResendCounting) {
      myInterval = setInterval(() => {
        if (resendSeconds > 0) {
          setResendSeconds(resendSeconds - 1)
        }
        if (resendSeconds === 0) {
          if (resendMinutes === 0) {
            setIsResendCounting(false)
            clearInterval(myInterval)
            resetCountDown()
          } else {
            setResendMinutes(resendMinutes - 1)
            setResendSeconds(59)
          }
        }
      }, 1000)
      return () => {
        clearInterval(myInterval)
      }
    }
  }, [isResendCounting, resendMinutes, resendSeconds, props.resendVerifyCountDownMinutes, resetCountDown])

  return (
    <>
      <div className={`flex flex-col gap-y-2`}>
        <div className="flex justify-between items-center">
          {props.label && (
            <span className="text-white text-sm font-semibold">
              {props.label} <span className="font-normal text-xs text-slate-300">{t('common.optional')}</span>
            </span>
          )}
        </div>
        <div className="flex flex-row justify-between rounded-full bg-white p-1 relative">
          <button
            className={'flex gap-x-2 items-center justify-center w-1/4 m-1 rounded-full bg-slate-200'}
            onClick={toggleModal}
            type="button"
          >
            <p>{props.selectedCountry.emoji}</p>
            <p className="text-sm">{props.selectedCountry.dialCode}</p>
          </button>
          <input
            onChange={onChangePhoneNumber}
            className="p-3 text-sm md:p-[17px] w-full rounded-full border-none outline-none"
            type="text"
            value={props.phoneNumber}
            placeholder={props.placeholder}
            maxLength={props.maxLength}
          />
        </div>
        {props.error && <div className="text-boldOrange -mt-1 font-medium">{props.error}</div>}
      </div>

      <VerifyPhoneModal
        isoCode={props.selectedCountry.isoCode}
        open={props.isVerifyPhoneModalOpened}
        phoneNumber={props.phoneNumber}
        dialCode={props.selectedCountry.dialCode}
        onClose={() => props.setIsVerifyPhoneModalOpened(false)}
        setIsValidPhone={props.setIsValidPhone}
        resendMinutes={resendMinutes}
        resendSeconds={resendSeconds}
        isResendCounting={isResendCounting}
        onResendClick={onSendVerifyClick}
        setIsResendCounting={setIsResendCounting}
        resetCountDown={() => resetCountDown()}
      />
      <SelectCountryModal
        open={isOpenCountry}
        onClose={() => setIsOpenCountry(false)}
        onSubmit={onChangeCountry}
        isoCode={props.selectedCountry.isoCode}
        dialCode={props.selectedCountry.dialCode}
      />
    </>
  )
}
